import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  User,
  SlidersHorizontal,
  Copy,
  RefreshCw,
  ShieldAlert,
  Building2,
  Pencil,
  KeyRound,
  Power,
  Trash2,
  Users,
  Eye,
  EyeOff,
  Sparkles,
  X,
  Check,
} from "lucide-react";
import {
  listPlatformUsers,
  createPlatformUser,
  updatePlatformUser,
  deletePlatformUser,
  listTenants,
  updateTenant,
  type CurrentUser,
  type PlatformUserItem,
  type Tenant,
} from "../../lib/api";


export function TeamManagementView({ token, user }: { token: string; user: CurrentUser }) {
  const queryClient = useQueryClient();
  const isSuperAdmin = user.role === "superadmin" || user.role === "platform_admin";

  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedTenantFilter, setSelectedTenantFilter] = useState("all");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Add User Form states
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [newUserRole, setNewUserRole] = useState<"admin" | "tenant" | "agent">("agent");
  const [selectedTenantId, setSelectedTenantId] = useState<string>(user.tenant_id || "");
  const [newIsActive, setNewIsActive] = useState(true);
  const [newCompanyName, setNewCompanyName] = useState("");

  // Edit User Form states
  const [editingUser, setEditingUser] = useState<PlatformUserItem | null>(null);
  const [editUsername, setEditUsername] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [editRole, setEditRole] = useState<"admin" | "tenant" | "agent">("agent");
  const [editIsActive, setEditIsActive] = useState<boolean>(true);
  const [editCompanyName, setEditCompanyName] = useState("");

  // Standalone Edit Company Modal
  const [isEditCompanyModalOpen, setIsEditCompanyModalOpen] = useState(false);
  const [standaloneCompanyName, setStandaloneCompanyName] = useState("");

  // Password Reset modal state
  const [resetPasswordUser, setResetPasswordUser] = useState<PlatformUserItem | null>(null);
  const [newResetPassword, setNewResetPassword] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);

  function generateRandomPassword() {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%&*";
    let pwd = "";
    for (let i = 0; i < 10; i++) {
      pwd += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return pwd;
  }

  function canManageUser(targetUser: PlatformUserItem) {
    if (isSuperAdmin) return true;
    return targetUser.tenant_id === user.tenant_id;
  }

  // Live Users Query from Backend
  const usersQuery = useQuery({
    queryKey: ["platform-users", token],
    queryFn: () => listPlatformUsers(token),
    refetchInterval: 5_000,
  });

  // Live Tenants Query
  const tenantsQuery = useQuery({
    queryKey: ["tenants", "list"],
    queryFn: () => listTenants(token),
  });

  const users = usersQuery.data || [];
  const tenants = tenantsQuery.data || [];
  const effectiveTenant = tenants.find((t) => t.id === (user.tenant_id || selectedTenantId)) || tenants[0];
  const currentCompanyName = user.tenant_name || effectiveTenant?.name || "MasterG Enterprise";

  function handleOpenEdit(targetUser: PlatformUserItem) {
    setEditingUser(targetUser);
    setEditUsername(targetUser.username);
    setEditRole(targetUser.role);
    const initialCompany =
      targetUser.tenant_name ||
      tenants.find((t) => t.id === targetUser.tenant_id)?.name ||
      user.tenant_name ||
      "MasterG Enterprise";
    setEditCompanyName(initialCompany);
    setEditIsActive(targetUser.is_active);
    setEditPassword("");
    setShowEditPassword(false);
  }

  // Standalone Company Name Update Mutation
  const updateCompanyMutation = useMutation({
    mutationFn: async (newName: string) => {
      const targetTenantId = user.tenant_id || (editingUser ? editingUser.tenant_id : undefined) || tenants[0]?.id;
      if (!targetTenantId) throw new Error("No company found to update");
      return updateTenant(token, targetTenantId, { name: newName });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform-users"] });
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      setIsEditCompanyModalOpen(false);
    },
    onError: (err: any) => {
      alert(`Failed to update company name: ${err.message || "Unknown error"}`);
    },
  });

  // Create User Mutation
  const createUserMutation = useMutation({
    mutationFn: async () => {
      return createPlatformUser(token, {
        username: newUsername.trim().toLowerCase(),
        password: newPassword.trim(),
        role: newUserRole,
        company_name: newUserRole === "admin" ? undefined : newCompanyName.trim() || undefined,
        is_active: newIsActive,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform-users"] });
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      setIsAddModalOpen(false);
      setNewUsername("");
      setNewPassword("");
      setShowNewPassword(false);
      setNewUserRole(isSuperAdmin ? "tenant" : "agent");
      setNewIsActive(true);
    },
    onError: (err: any) => {
      alert(`Failed to create user: ${err.message || "Unknown error"}`);
    },
  });

  // Update User Mutation
  const updateUserMutation = useMutation({
    mutationFn: async ({
      userId,
      payload,
    }: {
      userId: string;
      payload: Parameters<typeof updatePlatformUser>[2];
    }) => {
      return updatePlatformUser(token, userId, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform-users"] });
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      setEditingUser(null);
      setResetPasswordUser(null);
      setNewResetPassword("");
      setShowResetPassword(false);
      setEditPassword("");
      setShowEditPassword(false);
    },
    onError: (err: any) => {
      alert(`Failed to update user: ${err.message || "Unknown error"}`);
    },
  });

  // Delete User Mutation
  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => deletePlatformUser(token, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform-users"] });
    },
    onError: (err: any) => {
      alert(`Failed to delete user: ${err.message || "Unknown error"}`);
    },
  });


  const filteredUsers = users.filter((u) => {
    const matchesSearch =
      u.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (u.tenant_name || "").toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === "all" || u.role === roleFilter;
    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "Active" && u.is_active) ||
      (statusFilter === "Suspended" && !u.is_active);
    const matchesTenant =
      selectedTenantFilter === "all" || u.tenant_id === selectedTenantFilter;
    return matchesSearch && matchesRole && matchesStatus && matchesTenant;
  });

  function copyText(text: string) {
    navigator.clipboard.writeText(text);
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold mb-1">
            <span>Unlimited Team Agents Included</span>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <span className="font-mono font-bold">Zero Per-Seat Fees</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <User className="w-6 h-6 text-emerald-600" />
            Users Management
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Create, edit, and manage user accounts, access roles, and credentials. Each Company operates with its dedicated users.
          </p>

          <div className="flex items-center gap-2 mt-2">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-white border border-slate-200 text-xs shadow-2xs">
              <Building2 className="w-3.5 h-3.5 text-emerald-600" />
              <span className="text-slate-500 font-medium">Company:</span>
              <strong className="text-slate-800">{currentCompanyName}</strong>
              <button
                onClick={() => {
                  setStandaloneCompanyName(currentCompanyName);
                  setIsEditCompanyModalOpen(true);
                }}
                className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-emerald-700 transition-colors ml-1 cursor-pointer"
                title="Edit Company Name"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden sm:flex flex-col items-end text-right">
            <span className="text-[11px] text-slate-400 font-medium uppercase tracking-wider">Active Users</span>
            <span className="font-mono text-xs font-bold text-slate-800">{users.length} / ∞ (Unlimited Seats)</span>
          </div>
          <button
            onClick={() => {
              setNewUserRole(isSuperAdmin ? "tenant" : "agent");
              setSelectedTenantId(user.tenant_id || tenants[0]?.id || "");
              setNewCompanyName(currentCompanyName);
              setNewUsername("");
              setNewPassword("");
              setShowNewPassword(false);
              setNewIsActive(true);
              setIsAddModalOpen(true);
            }}
            className="btn-primary text-xs py-2 px-3.5 flex items-center gap-1.5 shadow-2xs"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Add User Account</span>
          </button>
        </div>
      </div>


      {/* Filter Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Search Box */}
          <div className="relative w-64">
            <input
              type="text"
              placeholder="Search by username or tenant..."
              className="modern-input w-full text-xs py-2"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Role Filter */}
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="filter-chip cursor-pointer outline-none font-semibold"
          >
            <option value="all">⊕ Role: All Roles</option>
            <option value="agent">Support / Sales Agent (Workspace)</option>
            <option value="tenant">Company Manager</option>
            {isSuperAdmin && <option value="admin">Super Admin (Global)</option>}
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-chip cursor-pointer outline-none"
          >
            <option value="all">⊕ Status: All</option>
            <option value="Active">Active</option>
            <option value="Suspended">Suspended</option>
          </select>

          {/* Linked Tenant Filter (Visible to Super Admin) */}
          {isSuperAdmin && (
            <select
              value={selectedTenantFilter}
              onChange={(e) => setSelectedTenantFilter(e.target.value)}
              className="filter-chip cursor-pointer outline-none"
            >
              <option value="all">🏢 Organization: All Organizations</option>
              {tenants.map((t: Tenant) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          )}

          {/* Clear Filters */}
          {(searchQuery || roleFilter !== "all" || statusFilter !== "all" || selectedTenantFilter !== "all") && (
            <button
              onClick={() => {
                setSearchQuery("");
                setRoleFilter("all");
                setStatusFilter("all");
                setSelectedTenantFilter("all");
              }}
              className="text-xs text-rose-600 hover:text-rose-700 font-semibold px-2 py-1"
            >
              Reset Filters
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => usersQuery.refetch()}
            className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5 shadow-2xs"
            title="Refresh database state"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${usersQuery.isFetching ? "animate-spin" : ""}`} />
            <span>Sync</span>
          </button>

          <button className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5 shadow-2xs">
            <SlidersHorizontal className="w-3.5 h-3.5 text-slate-500" />
            <span>View</span>
          </button>
        </div>
      </div>

      {/* Users Data Table */}
      <div className="modern-card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="modern-table">
            <thead>
              <tr>
                <th>User Account</th>
                <th>Access Role</th>
                <th>Company Name</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Created At</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {usersQuery.isLoading ? (
                <tr>
                  <td colSpan={7} className="text-center py-12">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                      <span className="text-xs text-slate-500 font-medium">Loading platform user directory...</span>
                      <span className="text-[11px] text-slate-400">Fetching Admin, Manager, and Agent role assignments</span>
                    </div>
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-slate-400 text-xs font-semibold">
                    No users found matching current filters.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((u: PlatformUserItem) => (
                  <tr key={u.id} className="hover:bg-slate-50/70 transition-colors">
                    {/* Username */}
                    <td>
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${
                          u.role === "admin"
                            ? "bg-purple-100 text-purple-800"
                            : u.role === "agent"
                            ? "bg-sky-100 text-sky-800"
                            : "bg-emerald-100 text-emerald-800"
                        }`}>
                          {u.username.slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <span className="font-bold text-slate-900 text-xs">{u.username}</span>
                          <div className="flex items-center gap-1 text-[10px] font-mono text-slate-400">
                            <button onClick={() => copyText(u.id)} title="Copy ID" className="hover:text-slate-700">
                              <Copy className="w-2.5 h-2.5" />
                            </button>
                            <span>{u.id.slice(0, 8)}...</span>
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Role */}
                    <td>
                      {u.role === "admin" ? (
                        <span className="px-2.5 py-1 rounded-md text-[11px] font-extrabold bg-purple-50 text-purple-700 border border-purple-200 inline-flex items-center gap-1">
                          <ShieldAlert className="w-3 h-3 text-purple-600" />
                          Super Admin
                        </span>
                      ) : u.role === "agent" ? (
                        <span className="px-2.5 py-1 rounded-md text-[11px] font-extrabold bg-sky-50 text-sky-700 border border-sky-200 inline-flex items-center gap-1">
                          <Users className="w-3 h-3 text-sky-600" />
                          Support Agent
                        </span>
                      ) : (
                        <span className="px-2.5 py-1 rounded-md text-[11px] font-extrabold bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
                          <Building2 className="w-3 h-3 text-emerald-600" />
                          Manager
                        </span>
                      )}
                    </td>

                    {/* Company Name */}
                    <td>
                      <span className="text-xs font-semibold text-slate-800">
                        {u.tenant_name || (u.role === "admin" ? "Global System" : "MasterG Enterprise")}
                      </span>
                    </td>

                    {/* Status */}
                    <td>
                      <span className={`status-pill ${u.is_active ? "status-pill--connected" : "status-pill--disconnected"}`}>
                        {u.is_active ? "Active" : "Suspended"}
                      </span>
                    </td>

                    {/* Last Login */}
                    <td className="font-mono text-xs text-slate-500">
                      {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}
                    </td>

                    {/* Created At */}
                    <td className="font-mono text-xs text-slate-500">
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>

                    {/* Actions */}
                    <td>
                      <div className="flex items-center gap-1.5">
                        {canManageUser(u) && (
                          <button
                            title="Edit User Account"
                            className="p-1.5 rounded-lg border border-slate-200 hover:border-blue-500 text-slate-500 hover:text-blue-600 transition-colors cursor-pointer"
                            onClick={() => handleOpenEdit(u)}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}

                        {canManageUser(u) && (
                          <button
                            title="Reset Password"
                            className="p-1.5 rounded-lg border border-slate-200 hover:border-amber-500 text-slate-500 hover:text-amber-600 transition-colors cursor-pointer"
                            onClick={() => {
                              setResetPasswordUser(u);
                              setNewResetPassword("");
                              setShowResetPassword(false);
                            }}
                          >
                            <KeyRound className="w-3.5 h-3.5" />
                          </button>
                        )}

                        {canManageUser(u) && (
                          <button
                            title={u.is_active ? "Suspend User" : "Activate User"}
                            className={`p-1.5 rounded-lg border transition-colors cursor-pointer ${
                              u.is_active
                                ? "border-slate-200 hover:border-amber-500 text-slate-500 hover:text-amber-600"
                                : "border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                            }`}
                            onClick={() => {
                              if (u.id === user.id) {
                                alert("You cannot suspend your own logged-in account.");
                                return;
                              }
                              updateUserMutation.mutate({
                                userId: u.id,
                                payload: { is_active: !u.is_active },
                              });
                            }}
                            disabled={updateUserMutation.isPending}
                          >
                            <Power className="w-3.5 h-3.5" />
                          </button>
                        )}

                        {canManageUser(u) && u.id !== user.id && (
                          <button
                            title="Delete User"
                            className="p-1.5 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                            onClick={() => {
                              if (window.confirm(`Are you sure you want to permanently delete user "${u.username}"?`)) {
                                deleteUserMutation.mutate(u.id);
                              }
                            }}
                            disabled={deleteUserMutation.isPending}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}

            </tbody>
          </table>
        </div>
      </div>

      {/* Add User Modal */}
      {isAddModalOpen && (
        <div className="modal-backdrop" onClick={() => setIsAddModalOpen(false)}>
          <div className="modal-card max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 mb-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <User className="w-4 h-4 text-emerald-600" />
                Add New User Account
              </h3>
              <button onClick={() => setIsAddModalOpen(false)} className="text-slate-400 hover:text-slate-700 text-xs font-bold cursor-pointer">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3.5">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  User Type / Access Role *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setNewUserRole("agent")}
                    className={`p-2.5 rounded-lg border text-left transition-all cursor-pointer ${
                      newUserRole === "agent"
                        ? "bg-sky-50 border-sky-500 text-sky-900 font-bold shadow-2xs"
                        : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 text-xs">
                      <Users className="w-4 h-4 text-sky-600" />
                      Support / Sales Agent
                    </div>
                    <p className="text-[10px] text-slate-500 font-normal mt-0.5">
                      Live Inbox, Contacts CRM &amp; Quick Replies
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setNewUserRole("tenant")}
                    className={`p-2.5 rounded-lg border text-left transition-all cursor-pointer ${
                      newUserRole === "tenant"
                        ? "bg-emerald-50 border-emerald-500 text-emerald-900 font-bold shadow-2xs"
                        : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 text-xs">
                      <Building2 className="w-4 h-4 text-emerald-600" />
                      Organization Manager
                    </div>
                    <p className="text-[10px] text-slate-500 font-normal mt-0.5">
                      Full workspace control (Team, Instances, Campaigns)
                    </p>
                  </button>

                  {isSuperAdmin && (
                    <button
                      type="button"
                      onClick={() => setNewUserRole("admin")}
                      className={`p-2.5 rounded-lg border text-left col-span-1 sm:col-span-2 transition-all cursor-pointer ${
                        newUserRole === "admin"
                          ? "bg-purple-50 border-purple-500 text-purple-900 font-bold shadow-2xs"
                          : "border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-center gap-1.5 text-xs">
                        <ShieldAlert className="w-4 h-4 text-purple-600" />
                        Super Admin (Global System)
                      </div>
                      <p className="text-[10px] text-slate-500 font-normal mt-0.5">Global platform administration across all tenants</p>
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Username *
                </label>
                <input
                  type="text"
                  placeholder="e.g. sales_rahul or support_agent1"
                  className="modern-input w-full text-xs font-mono"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  autoFocus
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-semibold text-slate-700">
                    Password *
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      const pwd = generateRandomPassword();
                      setNewPassword(pwd);
                      setShowNewPassword(true);
                    }}
                    className="text-[11px] text-emerald-600 hover:text-emerald-700 font-semibold flex items-center gap-1 cursor-pointer"
                  >
                    <Sparkles className="w-3 h-3" />
                    <span>Generate Strong Password</span>
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    placeholder="Minimum 6 characters"
                    className="modern-input w-full text-xs font-mono pr-9"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                    title={showNewPassword ? "Hide password" : "Show password"}
                  >
                    {showNewPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>

              {newUserRole !== "admin" && (
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Company Name
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={newCompanyName}
                      onChange={(e) => setNewCompanyName(e.target.value)}
                      placeholder="e.g. MasterG Enterprise"
                      className="modern-input w-full text-xs font-semibold pl-8"
                    />
                    <Building2 className="w-4 h-4 text-emerald-600 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1">
                    Company name for this user account
                  </p>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Account Status
                </label>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-slate-700">
                    <input
                      type="radio"
                      name="new_status"
                      checked={newIsActive}
                      onChange={() => setNewIsActive(true)}
                      className="accent-emerald-600"
                    />
                    <span>Active (Enabled)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-slate-700">
                    <input
                      type="radio"
                      name="new_status"
                      checked={!newIsActive}
                      onChange={() => setNewIsActive(false)}
                      className="accent-emerald-600"
                    />
                    <span>Suspended (Disabled)</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button className="btn-secondary text-xs cursor-pointer" onClick={() => setIsAddModalOpen(false)}>
                  Cancel
                </button>
                <button
                  className="btn-primary text-xs cursor-pointer"
                  onClick={() => createUserMutation.mutate()}
                  disabled={!newUsername.trim() || newPassword.trim().length < 6 || createUserMutation.isPending}
                >
                  {createUserMutation.isPending ? "Creating..." : "Save User Account"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="modal-backdrop" onClick={() => setEditingUser(null)}>
          <div className="modal-card max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 mb-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Pencil className="w-4 h-4 text-blue-600" />
                Edit User: {editingUser.username}
              </h3>
              <button onClick={() => setEditingUser(null)} className="text-slate-400 hover:text-slate-700 text-xs font-bold cursor-pointer">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3.5">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Username *
                </label>
                <input
                  type="text"
                  className="modern-input w-full text-xs font-mono"
                  value={editUsername}
                  onChange={(e) => setEditUsername(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  User Role *
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setEditRole("agent")}
                    className={`p-2.5 rounded-lg border text-left transition-all cursor-pointer ${
                      editRole === "agent"
                        ? "bg-sky-50 border-sky-500 text-sky-900 font-bold"
                        : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 text-xs">
                      <Users className="w-4 h-4 text-sky-600" />
                      Support Agent
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setEditRole("tenant")}
                    className={`p-2.5 rounded-lg border text-left transition-all cursor-pointer ${
                      editRole === "tenant"
                        ? "bg-emerald-50 border-emerald-500 text-emerald-900 font-bold"
                        : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 text-xs">
                      <Building2 className="w-4 h-4 text-emerald-600" />
                      Manager
                    </div>
                  </button>

                  {isSuperAdmin && (
                    <button
                      type="button"
                      onClick={() => setEditRole("admin")}
                      className={`p-2.5 rounded-lg border text-left col-span-2 transition-all cursor-pointer ${
                        editRole === "admin"
                          ? "bg-purple-50 border-purple-500 text-purple-900 font-bold"
                          : "border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-center gap-1.5 text-xs">
                        <ShieldAlert className="w-4 h-4 text-purple-600" />
                        Super Admin (Global System)
                      </div>
                    </button>
                  )}
                </div>
              </div>

              {editRole !== "admin" && (
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Company Name
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={editCompanyName}
                      onChange={(e) => setEditCompanyName(e.target.value)}
                      placeholder="e.g. MasterG Enterprise"
                      className="modern-input w-full text-xs font-semibold pl-8"
                    />
                    <Building2 className="w-4 h-4 text-emerald-600 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1">
                    Edit company name for this organization
                  </p>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-semibold text-slate-700">
                    Change Password (Optional)
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      const pwd = generateRandomPassword();
                      setEditPassword(pwd);
                      setShowEditPassword(true);
                    }}
                    className="text-[11px] text-emerald-600 hover:text-emerald-700 font-semibold flex items-center gap-1 cursor-pointer"
                  >
                    <Sparkles className="w-3 h-3" />
                    <span>Generate</span>
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showEditPassword ? "text" : "password"}
                    placeholder="Leave empty to keep existing password"
                    className="modern-input w-full text-xs font-mono pr-9"
                    value={editPassword}
                    onChange={(e) => setEditPassword(e.target.value)}
                  />
                  {editPassword && (
                    <button
                      type="button"
                      onClick={() => setShowEditPassword(!showEditPassword)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                    >
                      {showEditPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Account Status
                </label>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-slate-700">
                    <input
                      type="radio"
                      name="edit_status"
                      checked={editIsActive}
                      onChange={() => setEditIsActive(true)}
                      className="accent-emerald-600"
                    />
                    <span>Active (Enabled)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-slate-700">
                    <input
                      type="radio"
                      name="edit_status"
                      checked={!editIsActive}
                      onChange={() => setEditIsActive(false)}
                      className="accent-emerald-600"
                    />
                    <span>Suspended (Disabled)</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button className="btn-secondary text-xs cursor-pointer" onClick={() => setEditingUser(null)}>
                  Cancel
                </button>
                <button
                  className="btn-primary text-xs cursor-pointer"
                  onClick={() => {
                    updateUserMutation.mutate({
                      userId: editingUser.id,
                      payload: {
                        username: editUsername.trim().toLowerCase() !== editingUser.username.toLowerCase() ? editUsername.trim().toLowerCase() : undefined,
                        role: editRole,
                        company_name: editRole === "admin" ? undefined : editCompanyName.trim() || undefined,
                        is_active: editIsActive,
                        password: editPassword.trim() ? editPassword.trim() : undefined,
                      },
                    });
                  }}
                  disabled={!editUsername.trim() || updateUserMutation.isPending}
                >
                  {updateUserMutation.isPending ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPasswordUser && (
        <div className="modal-backdrop" onClick={() => setResetPasswordUser(null)}>
          <div className="modal-card max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 mb-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <KeyRound className="w-4 h-4 text-amber-600" />
                Reset Password
              </h3>
              <button onClick={() => setResetPasswordUser(null)} className="text-slate-400 hover:text-slate-700 text-xs font-bold cursor-pointer">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3.5">
              <p className="text-xs text-slate-600">
                Set a new password for user <strong>{resetPasswordUser.username}</strong>:
              </p>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-semibold text-slate-700">
                    New Password *
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      const pwd = generateRandomPassword();
                      setNewResetPassword(pwd);
                      setShowResetPassword(true);
                    }}
                    className="text-[11px] text-emerald-600 hover:text-emerald-700 font-semibold flex items-center gap-1 cursor-pointer"
                  >
                    <Sparkles className="w-3 h-3" />
                    <span>Generate</span>
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showResetPassword ? "text" : "password"}
                    placeholder="Minimum 6 characters"
                    className="modern-input w-full text-xs font-mono pr-9"
                    value={newResetPassword}
                    onChange={(e) => setNewResetPassword(e.target.value)}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetPassword(!showResetPassword)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                  >
                    {showResetPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button className="btn-secondary text-xs cursor-pointer" onClick={() => setResetPasswordUser(null)}>
                  Cancel
                </button>
                <button
                  className="btn-primary text-xs cursor-pointer"
                  onClick={() => {
                    updateUserMutation.mutate({
                      userId: resetPasswordUser.id,
                      payload: { password: newResetPassword.trim() },
                    });
                  }}
                  disabled={newResetPassword.trim().length < 6 || updateUserMutation.isPending}
                >
                  {updateUserMutation.isPending ? "Resetting..." : "Update Password"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Standalone Edit Company Name Modal */}
      {isEditCompanyModalOpen && (
        <div className="modal-backdrop" onClick={() => setIsEditCompanyModalOpen(false)}>
          <div className="modal-card max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 mb-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-emerald-600" />
                Edit Company Name
              </h3>
              <button
                onClick={() => setIsEditCompanyModalOpen(false)}
                className="text-slate-400 hover:text-slate-700 text-xs font-bold cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3.5">
              <p className="text-xs text-slate-600">
                Update the official company name across the entire platform:
              </p>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Company Name *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={standaloneCompanyName}
                    onChange={(e) => setStandaloneCompanyName(e.target.value)}
                    placeholder="e.g. MasterG Enterprise"
                    className="modern-input w-full text-xs font-semibold pl-8"
                    autoFocus
                  />
                  <Building2 className="w-4 h-4 text-emerald-600 absolute left-2.5 top-1/2 -translate-y-1/2" />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  className="btn-secondary text-xs cursor-pointer"
                  onClick={() => setIsEditCompanyModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary text-xs cursor-pointer flex items-center gap-1.5"
                  onClick={() => updateCompanyMutation.mutate(standaloneCompanyName.trim())}
                  disabled={!standaloneCompanyName.trim() || updateCompanyMutation.isPending}
                >
                  {updateCompanyMutation.isPending ? (
                    <span>Saving...</span>
                  ) : (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      <span>Save Company Name</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

